import React from "react";
import { CircularProgress, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import AppState from "../../store/AppState";
import {
  selectAllProductsFetched,
  selectProductFetched,
} from "../../store/product/ProductSelectors";
import {
  selectIsModalOpen,
  selectModalVariable,
} from "../../store/modal/ModalSelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { setModalState } from "../../store/modal/ModalActions";
import CustomDialog from "../common/DialogContainer";
import { getPrice, getProductModalTitle } from "utils/ProductUtil";
import {
  selectProductGroupComponentsProductIds,
  selectProductGroupComponentsLoading,
  selectProductGroupComponents,
} from "../../store/product-group/ProductGroupComponentsSelectors";
import {
  ProductGroupStyledDiv,
  StyledBox,
  StyledDiv,
  styleClasses,
} from "./ProductGroupStyles";
import ProductGroupComponents from "./ProductGroupComponents";
import ButtonComponent from "components/common/ButtonComponent";
import { useIntl } from "react-intl";
import { useStyles } from "./ProductGroupStyles";
import { resetProductGroupComponents } from "store/product-group/ProductGroupComponentsActions";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
export const ProductGroupManagementModalId = "ProductGroupManagementModal";

const ProductGroupManagement: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { divActionBtn, rightSection } = useStyles();
  const productId = useSelector((state: AppState) =>
    selectModalVariable(state, ProductGroupManagementModalId, "productId"),
  );
  const fetchedProduct = useSelector((state: AppState) =>
    selectProductFetched(state, productId),
  );
  const allProductsData = useSelector((state: AppState) =>
    selectAllProductsFetched(state),
  );
  const productGroupComponentsList = useSelector((state: AppState) =>
    selectProductGroupComponentsProductIds(state),
  );
  const productGroupComponents = useSelector((state: AppState) =>
    selectProductGroupComponents(state),
  );
  const productGroupComponentsData =
    productGroupComponentsList?.map(
      (productId) => allProductsData[productId],
    ) || [];
  const productGroupComponentsLoading = useSelector((state: AppState) =>
    selectProductGroupComponentsLoading(state),
  );
  const storeId = useSelector(selectCurrentStoreId);
  const currencySymbol = useCurrencySymbol(fetchedProduct?.currencyCode);
  const lowPrice = fetchedProduct?.lowPrice;
  const highPrice = fetchedProduct?.highPrice;
  const productGroupPrice = getPrice(lowPrice, highPrice, currencySymbol);

  const isModalOpen = useSelector((state: AppState) =>
    selectIsModalOpen(state, ProductGroupManagementModalId),
  );

  const handleClose = () => {
    dispatch(setModalState(ProductGroupManagementModalId, false));
    dispatch(resetProductGroupComponents());
  };

  const productModalTitle = getProductModalTitle(fetchedProduct);
  const productGroupModalTitle = () => {
    return (
      <Typography variant="inherit">
        Product Group Mgmt - {productModalTitle}
      </Typography>
    );
  };

  const productGroupModalContent = () => {
    return (
      <ProductGroupStyledDiv className={styleClasses.paper}>
        {productGroupComponentsLoading ? (
          <StyledDiv className={styleClasses.overlay}>
            <CircularProgress />
          </StyledDiv>
        ) : (
          <ProductGroupStyledDiv className={styleClasses.paper}>
            <ProductGroupStyledDiv className={styleClasses.leftSection}>
              <ProductGroupComponents
                product={fetchedProduct}
                isColumn={false}
                storeId={storeId ?? ""}
                productGroupPrice={productGroupPrice}
              />
            </ProductGroupStyledDiv>
            <ProductGroupStyledDiv className={styleClasses.mainContent}>
              {productGroupComponentsData?.length > 0 && (
                <Typography variant="productGroupComponentsTitle">
                  Products
                </Typography>
              )}
              <StyledBox className={styleClasses.mainSection}>
                {productGroupComponentsData?.length > 0 &&
                  productGroupComponentsData?.map((product, productIndex) => {
                    const price = getPrice(
                      product?.lowPrice,
                      product?.highPrice,
                      currencySymbol,
                    );
                    return (
                      <ProductGroupComponents
                        product={product}
                        code={product?.code}
                        price={price}
                        isColumn={true}
                        index={productIndex}
                        key={productIndex.toString()}
                        storeId={storeId ?? ""}
                        isPublished={product?.isPublished ?? false}
                        productGroupComponents={productGroupComponents}
                        hasComponentQuantity={
                          fetchedProduct?.hasComponentQuantity ?? false
                        }
                      />
                    );
                  })}
              </StyledBox>
            </ProductGroupStyledDiv>
            <div style={rightSection as any}>
              <div style={divActionBtn as any}>
                <ButtonComponent
                  color="primary"
                  variant="contained"
                  onClick={() => handleClose()}
                >
                  {intl.formatMessage({
                    id: "general.close",
                    defaultMessage: "Close",
                  })}
                </ButtonComponent>
              </div>
            </div>
          </ProductGroupStyledDiv>
        )}
      </ProductGroupStyledDiv>
    );
  };

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={() => handleClose()}
      title={productGroupModalTitle()}
      fullWidth
      maxWidth={"lg"}
      onClick={(e) => e.preventDefault()}
    >
      {productGroupModalContent()}
    </CustomDialog>
  );
};

export default React.memo(ProductGroupManagement);
